import React, { createContext, useMemo, useState } from 'react';

import { Outlet, useLocation, useParams } from 'react-router-dom';

import type { WithdrawalEligibilityType } from './data';

export type FlowStep =
  | 'eligibility_verification'
  | 'distribution_amount'
  | 'withholding'
  | 'disbursement_method'
  | 'bank_account_selection'
  | 'check_address'
  | 'confirmation';

const getSteps = (withdrawalType: WithdrawalEligibilityType | null): FlowStep[] => {
  switch (withdrawalType) {
    case 'TERMINATION':
    case 'IN_SERVICE_ROLLOVER':
      return [
        'eligibility_verification',
        'distribution_amount',
        'withholding',
        'disbursement_method',
        'bank_account_selection',
        'confirmation',
      ];
    case 'IN_SERVICE_AGE_BASED':
      return ['distribution_amount', 'withholding', 'disbursement_method', 'bank_account_selection', 'confirmation'];
    default:
      return [];
  }
};

type DistributionFlowContextValue = {
  flowId: string;
  withdrawalType: WithdrawalEligibilityType | null;
  setWithdrawalEligibilityType: (type: WithdrawalEligibilityType) => void;
  nextStep: FlowStep | null;
  prevStep: FlowStep | null;
};

export const DistributionFlowContext = createContext<DistributionFlowContextValue | null>(null);

const DistributionFlowProvider: React.FC = () => {
  const { flowId } = useParams<{ flowId: string }>();
  const { pathname } = useLocation();
  // TODO: remove default — will be set by eligibility_verification step
  const [withdrawalType, setWithdrawalEligibilityType] = useState<WithdrawalEligibilityType | null>('TERMINATION');

  const currentStep = pathname.split('/').pop() as FlowStep | undefined;

  const { nextStep, prevStep } = useMemo(() => {
    if (!currentStep) return { nextStep: null, prevStep: null };
    const steps = getSteps(withdrawalType);
    const idx = steps.indexOf(currentStep);
    return {
      nextStep: idx >= 0 && idx < steps.length - 1 ? steps[idx + 1] : null,
      prevStep: idx > 0 ? steps[idx - 1] : null,
    };
  }, [withdrawalType, currentStep]);

  const value = useMemo(
    () => ({ flowId: flowId!, withdrawalType, setWithdrawalEligibilityType, nextStep, prevStep }),
    [flowId, withdrawalType, nextStep, prevStep]
  );

  return (
    <DistributionFlowContext.Provider value={value}>
      <Outlet />
    </DistributionFlowContext.Provider>
  );
};

export default DistributionFlowProvider;
